/**
 * alert-feed.ts — visual alert feed (v2M8.3, UI_UX §1.5 + §7).
 *
 * Persistent right-side scrolling feed of recent events. Max 5 entries
 * visible at once; older entries drop off the bottom. Each toast is
 * click-jumpable — clicking centres the viewport on the source. Audio
 * cue per category (Red/Amber/Blue/Green) plays via `getAudioEngine`.
 *
 * v2M8.3 ships:
 *   - DOM toast list (top-right anchored, stacks downward).
 *   - 6 sim event hooks via diff-against-prev-frame:
 *       Red   — own planet flipped (ownership change), own planet's
 *               Loyalty crossing below 30, own ship destroyed.
 *       Green — research completed, own structure initial-build
 *               completed, own ship spawned.
 *   - Audio cue per category fired automatically on spawn.
 *   - Click-to-centre callback (caller wires camera focus).
 *
 * Deferred (no v1 sim hooks yet — cues registered + spawn API works
 * but no auto-spawns until those gameplay systems land):
 *   - Amber — low-resource warnings, idle Constructor, etc.
 *   - Blue — diplomacy events.
 *   - Configurable rules / mute-source / auto-pause / flash border —
 *     v3M11 polish.
 *
 * Determinism: alerts are pure derivatives of game-state transitions,
 * never written back to GameState or the RNG. Same diff-and-detect
 * pattern as `vfx.ts` / `audio/diff.ts`. Wall-clock used only for
 * toast fade-out timing (purely visual).
 *
 * Viewer scope: the feed is **player-only** — F10 enemy POV doesn't
 * remap which empire's events surface. Audio §11's redundant-
 * reinforcement principle: the player should always know their
 * own situation regardless of which fog is currently rendered.
 */

import {
  EMPIRE_NEUTRAL_ID,
  type EmpireId,
  type EntityId,
  type GameState,
  type PlanetId,
  type ShipInstanceId,
} from '../state/types';
import type { InterfaceSettings } from '../settings/types';
import { getAudioEngine } from '../audio';
import { getTeamRoster } from '../game/teams';

/** Toast severity / category — drives colour + audio cue. */
export type AlertCategory = 'red' | 'amber' | 'blue' | 'green';

/** Optional click-to-centre target. */
export interface AlertSource {
  kind: 'planet' | 'system' | 'ship';
  id: number;
}

/** Caller-provided spawn payload — `category` + human-readable message. */
export interface AlertSpawn {
  category: AlertCategory;
  message: string;
  source?: AlertSource;
}

export interface AlertFeedCallbacks {
  /**
   * Click handler when the player clicks a toast with a source. Caller
   * wires this to camera-focus / view-mode switching as it sees fit.
   * Toasts without a source are not click-jumpable.
   */
  onJumpTo?: (source: AlertSource) => void;
  /**
   * v4M1.5 — live accessor for the `interface` Settings sub-object.
   * Read per-spawn so the alert-feed picks up:
   *   - the player's max-visible cap (3 / 5 / 7),
   *   - the muted alert sources list (filters source-bearing alerts).
   * Optional so test scaffolds without a settings layer can omit it;
   * defaults match the prior hardcoded behaviour (5 visible, no mutes).
   */
  getInterfaceSettings?: () => InterfaceSettings;
}

export interface AlertFeedHandle {
  /** Manually push an alert (skips the diff path). Plays the audio cue. */
  spawn(alert: AlertSpawn): void;
  /**
   * Per-frame diff entry point. Called from the session render loop with
   * the current GameState; spawns alerts for any sim transition observed
   * since the last call. Idempotent within a single sim tick.
   */
  update(state: GameState, viewerEmpireId: EmpireId): void;
  destroy(): void;
}

/** Legacy / fallback cap used when no `getInterfaceSettings` is wired. v4M1.5
 * promoted this to a Settings field. */
const MAX_VISIBLE_DEFAULT = 5;
/** Toast lifetime before auto-fade (wall-clock ms). */
const TOAST_LIFETIME_MS = 6000;
/** Fade duration tail (overlapping with lifetime). */
const TOAST_FADE_MS = 800;
/** Loyalty threshold below which Red alert fires (UI_UX §7 "Loyalty < 30"). */
const LOYALTY_CRITICAL = 30;

/** Per-category palette — sourced from GRAPHICS §2.1 / UI_UX §1.5. */
const CATEGORY_COLOR: Record<AlertCategory, { border: string; bg: string; text: string }> = {
  red: { border: '#D24548', bg: 'rgba(58, 14, 18, 0.92)', text: '#F2C0C2' },
  amber: { border: '#F0A93B', bg: 'rgba(58, 36, 12, 0.92)', text: '#F2D9A8' },
  blue: { border: '#4FB3FF', bg: 'rgba(12, 32, 58, 0.92)', text: '#BDDDFA' },
  green: { border: '#63C77C', bg: 'rgba(14, 44, 22, 0.92)', text: '#BFE5C8' },
};

const CATEGORY_CUE: Record<AlertCategory, 'alert:red' | 'alert:amber' | 'alert:blue' | 'alert:green'> = {
  red: 'alert:red',
  amber: 'alert:amber',
  blue: 'alert:blue',
  green: 'alert:green',
};

/**
 * Per-session diff snapshot. Mirrors the `VfxState` / `DiffState`
 * pattern — every value here is captured against the previous tick
 * so transitions can be detected without sim involvement.
 */
interface AlertDiffState {
  lastObservedTick: number;
  /** Per-planet owner snapshot (null = uninhabited). */
  prevPlanetOwners: Map<PlanetId, EmpireId | null>;
  /** Per-planet loyalty snapshot — used for the < 30 transition. */
  prevPlanetLoyalty: Map<PlanetId, number>;
  /** Tech names the viewer empire has researched. */
  prevResearchedTechs: Set<string>;
  /** Per-structure level snapshot (catches level transitions on completion). */
  prevStructureLevel: Map<EntityId, number>;
  /** Live ship-id snapshot — drives "ship destroyed" + "ship built" detection. */
  prevShipIds: Set<ShipInstanceId>;
  /** v3M21.6 — per-planet `federatedWith` snapshot for the
   *  `diplomatic.federated` / `diplomatic.federationLost` transitions. */
  prevPlanetFederatedWith: Map<PlanetId, EmpireId | null>;
  /** v3M21.6 — per-planet `vassalOf` snapshot for the
   *  `diplomatic.vassalised` / `diplomatic.vassalLost` transitions. */
  prevPlanetVassalOf: Map<PlanetId, EmpireId | null>;
  /** v3M21.6 — per-planet `tradingWith` snapshot. Stored as a Set per
   *  planet so the `diplomatic.tradeUnlocked` detection can fire when
   *  the viewer's id appears in the array (added via `invokeTrade`). */
  prevPlanetTradingWith: Map<PlanetId, Set<EmpireId>>;
  /** v4M1.6 — stalemate detection: last tick a significant transition
   *  was observed (planet ownership flip). Used to detect "no
   *  territorial changes for N ticks." Reset to current tick whenever
   *  a transition is observed; initialised to 0 so the timer counts
   *  from game start. */
  lastTransitionTick: number;
  /** v4M1.6 — stalemate alert fire-tick. Null when no alert is
   *  pending-or-active; set when the alert fires to prevent spam.
   *  Cleared back to null on the next observed transition so future
   *  stalemate windows trigger fresh alerts. */
  stalemateAlertFiredAtTick: number | null;
  /** v4M3.4 — per-team aliveness snapshot. A team is alive iff at
   *  least one of its member empires holds a planet. We compare the
   *  current tick's aliveness against this snapshot so the falling
   *  edge (alive → eliminated) fires exactly once per team. Solo
   *  empires (`teamId === null`) are omitted — the existing per-empire
   *  toasts cover them. */
  prevTeamAlive: Map<number, boolean>;
}

function createDiffState(): AlertDiffState {
  return {
    lastObservedTick: -1,
    prevPlanetOwners: new Map(),
    prevPlanetLoyalty: new Map(),
    prevResearchedTechs: new Set(),
    prevStructureLevel: new Map(),
    prevShipIds: new Set(),
    prevPlanetFederatedWith: new Map(),
    prevPlanetVassalOf: new Map(),
    prevPlanetTradingWith: new Map(),
    lastTransitionTick: 0,
    stalemateAlertFiredAtTick: null,
    prevTeamAlive: new Map(),
  };
}

export function mountAlertFeed(
  parent: HTMLElement,
  callbacks: AlertFeedCallbacks = {},
): AlertFeedHandle {
  const container = document.createElement('div');
  container.id = 'alert-feed';
  Object.assign(container.style, {
    position: 'fixed',
    // MFix5.9 — relocated top-right -> top-left so alerts don't block
    // the detail panel + planet/ship info on the right side. Top
    // offset stays at 64px so the HUD breathes; vertical overlap with
    // the v3M13 Dev Toolbox (top: 12px, also top-left) is acceptable
    // because the Toolbox is dev-only and rarely open during play.
    top: '64px',
    left: '16px',
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    zIndex: '900',
    pointerEvents: 'none',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
  } as Partial<CSSStyleDeclaration>);
  parent.appendChild(container);

  const diff = createDiffState();
  let firstFrame = true;

  function spawn(alert: AlertSpawn): void {
    // v4M1.5 — Interface settings gate. Drop the alert before any DOM
    // or audio side-effects if its source is muted; honour the player's
    // configured max-visible cap when pruning.
    const iface = callbacks.getInterfaceSettings?.();
    if (alert.source && iface) {
      for (const m of iface.mutedAlertSources) {
        if (m.kind === alert.source.kind && m.id === alert.source.id) return;
      }
    }
    const maxVisible = iface?.alertFeedMaxVisible ?? MAX_VISIBLE_DEFAULT;
    pruneOldest(container, maxVisible);
    const toast = makeToast(alert, callbacks);
    container.appendChild(toast);
    getAudioEngine().playCue(CATEGORY_CUE[alert.category]);
    // Schedule auto-fade. Wall-clock is fine — purely visual.
    const fadeAt = TOAST_LIFETIME_MS - TOAST_FADE_MS;
    setTimeout(() => {
      toast.style.transition = `opacity ${TOAST_FADE_MS}ms ease`;
      toast.style.opacity = '0';
    }, fadeAt);
    setTimeout(() => {
      toast.remove();
    }, TOAST_LIFETIME_MS);
  }

  function update(state: GameState, viewerEmpireId: EmpireId): void {
    if (state.tick === diff.lastObservedTick) return;
    diff.lastObservedTick = state.tick;

    // First frame: just snapshot, don't fire alerts (everything would
    // look like a "new" event the first time we observe it).
    if (firstFrame) {
      snapshotAll(diff, state, viewerEmpireId);
      firstFrame = false;
      return;
    }

    detectPlanetEvents(state, viewerEmpireId, diff, spawn);
    detectDiplomacyEvents(state, viewerEmpireId, diff, spawn);
    detectResearchComplete(state, viewerEmpireId, diff, spawn);
    detectStructureComplete(state, viewerEmpireId, diff, spawn);
    detectShipEvents(state, viewerEmpireId, diff, spawn);
    detectStalemate(state, diff, spawn);
    detectCommandRejections(state, viewerEmpireId, spawn);
    detectTeamEliminated(state, diff, spawn);
    snapshotAll(diff, state, viewerEmpireId);
  }

  return {
    spawn,
    update,
    destroy(): void {
      container.remove();
    },
  };
}

function pruneOldest(container: HTMLElement, maxVisible: number): void {
  while (container.children.length >= maxVisible) {
    container.firstElementChild?.remove();
  }
}

function makeToast(alert: AlertSpawn, callbacks: AlertFeedCallbacks): HTMLElement {
  const palette = CATEGORY_COLOR[alert.category];
  const toast = document.createElement('div');
  Object.assign(toast.style, {
    pointerEvents: 'auto',
    padding: '8px 12px',
    borderLeft: `3px solid ${palette.border}`,
    background: palette.bg,
    color: palette.text,
    fontSize: '12px',
    lineHeight: '1.35',
    letterSpacing: '0.02em',
    borderRadius: '2px',
    cursor: alert.source ? 'pointer' : 'default',
    boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
  } as Partial<CSSStyleDeclaration>);
  toast.textContent = alert.message;
  toast.dataset.noSound = ''; // suppress the global ui:click cue when clicked
  if (alert.source && callbacks.onJumpTo) {
    const src = alert.source;
    toast.addEventListener('pointerdown', () => callbacks.onJumpTo!(src));
  }
  return toast;
}

// ── Event detection ───────────────────────────────────────────────────

function detectPlanetEvents(
  state: GameState,
  viewerEmpireId: EmpireId,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  for (const planet of state.planets.values()) {
    const prevOwner = diff.prevPlanetOwners.get(planet.id);
    const prevLoyalty = diff.prevPlanetLoyalty.get(planet.id);

    // Ownership flipped — fire only when the viewer was the owner
    // OR is the new owner (gain notification too). v3M21.6 — gain
    // FROM the synthetic neutral surfaces as `diplomatic.annexed`
    // ("[Planet] has been annexed.") instead of the generic "secured"
    // copy, per `DIPLOMACY.md §10`.
    if (prevOwner !== undefined && prevOwner !== planet.ownerEmpireId) {
      const wasOwn = prevOwner === viewerEmpireId;
      const isOwn = planet.ownerEmpireId === viewerEmpireId;
      if (wasOwn) {
        spawn({
          category: 'red',
          message: `${planet.name} fell out of our control`,
          source: { kind: 'planet', id: planet.id },
        });
      } else if (isOwn) {
        const wasNeutral = prevOwner === EMPIRE_NEUTRAL_ID;
        spawn({
          category: wasNeutral ? 'blue' : 'green',
          message: wasNeutral
            ? `${planet.name} has been annexed`
            : `${planet.name} secured`,
          source: { kind: 'planet', id: planet.id },
        });
      }
    }

    // Loyalty crossed below the critical threshold on a viewer-owned
    // planet. Fire only on the falling-edge crossing (not every tick
    // the value sits below).
    if (
      planet.ownerEmpireId === viewerEmpireId &&
      prevLoyalty !== undefined &&
      prevLoyalty >= LOYALTY_CRITICAL &&
      planet.loyalty < LOYALTY_CRITICAL
    ) {
      spawn({
        category: 'red',
        message: `${planet.name} loyalty critical (${planet.loyalty})`,
        source: { kind: 'planet', id: planet.id },
      });
    }
  }
}

/**
 * v3M21.6 — diplomacy state-transition events (`DIPLOMACY.md §10`).
 *
 * Six event kinds, all fired exactly once on the rising / falling
 * edge so the player sees outcome-flip moments rather than steady
 * states:
 *
 *   - `diplomatic.tradeUnlocked`   — viewer's id newly appears in
 *                                    `Planet.tradingWith`.
 *   - `diplomatic.federated`       — `Planet.federatedWith` becomes
 *                                    the viewer.
 *   - `diplomatic.federationLost`  — `Planet.federatedWith` was the
 *                                    viewer, now isn't (rival broke,
 *                                    `clearFederation`, or the planet
 *                                    flipped owner).
 *   - `diplomatic.vassalised`      — `Planet.vassalOf` becomes the
 *                                    viewer.
 *   - `diplomatic.vassalLost`      — `Planet.vassalOf` was the viewer,
 *                                    now isn't.
 *   - `diplomatic.annexed`         — viewer-side annex of a neutral
 *                                    is fired in `detectPlanetEvents`
 *                                    (folded into the ownership-flip
 *                                    pass to share the prev-owner
 *                                    snapshot).
 *
 * All toasts use the `blue` category per UI_UX §1.5 — diplomacy is
 * the canonical Blue channel.
 */
function detectDiplomacyEvents(
  state: GameState,
  viewerEmpireId: EmpireId,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  for (const planet of state.planets.values()) {
    // Trade — viewer just gained a Trade row on this planet.
    const prevTrading = diff.prevPlanetTradingWith.get(planet.id);
    const isTradingNow = planet.tradingWith.includes(viewerEmpireId);
    const wasTrading = prevTrading?.has(viewerEmpireId) ?? false;
    if (prevTrading !== undefined && isTradingNow && !wasTrading) {
      spawn({
        category: 'blue',
        message: `Trade unlocked with ${planet.name}`,
        source: { kind: 'planet', id: planet.id },
      });
    }

    // Federation — viewer became the federation partner.
    const prevFed = diff.prevPlanetFederatedWith.get(planet.id);
    if (prevFed !== undefined) {
      const wasFed = prevFed === viewerEmpireId;
      const isFed = planet.federatedWith === viewerEmpireId;
      if (!wasFed && isFed) {
        spawn({
          category: 'blue',
          message: `Federation established with ${planet.name}`,
          source: { kind: 'planet', id: planet.id },
        });
      } else if (wasFed && !isFed) {
        spawn({
          category: 'blue',
          message: `Lost federation with ${planet.name}`,
          source: { kind: 'planet', id: planet.id },
        });
      }
    }

    // Vassal — viewer became the suzerain.
    const prevVassal = diff.prevPlanetVassalOf.get(planet.id);
    if (prevVassal !== undefined) {
      const wasVassal = prevVassal === viewerEmpireId;
      const isVassal = planet.vassalOf === viewerEmpireId;
      if (!wasVassal && isVassal) {
        spawn({
          category: 'blue',
          message: `${planet.name} is now your vassal`,
          source: { kind: 'planet', id: planet.id },
        });
      } else if (wasVassal && !isVassal) {
        spawn({
          category: 'blue',
          message: `Lost vassalage of ${planet.name}`,
          source: { kind: 'planet', id: planet.id },
        });
      }
    }
  }
}

function detectResearchComplete(
  state: GameState,
  viewerEmpireId: EmpireId,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  const empire = state.empires.get(viewerEmpireId);
  if (!empire) return;
  for (const tech of empire.researchedTechs) {
    if (!diff.prevResearchedTechs.has(tech)) {
      spawn({ category: 'green', message: `Research complete: ${tech}` });
    }
  }
}

function detectStructureComplete(
  state: GameState,
  viewerEmpireId: EmpireId,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  for (const struct of state.structures.values()) {
    const planet = state.planets.get(struct.planetId);
    if (!planet || planet.ownerEmpireId !== viewerEmpireId) continue;
    const prevLevel = diff.prevStructureLevel.get(struct.id);
    // Initial-build completion only — upgrades stay log-only to avoid
    // toast spam. UI_UX §7 "low-value events log-only" applies here.
    if (prevLevel === 0 && struct.level === 1 && struct.buildTicksRemaining === 0) {
      spawn({
        category: 'green',
        message: `${humaniseKind(struct.kind)} complete on ${planet.name}`,
        source: { kind: 'planet', id: planet.id },
      });
    }
  }
}

function detectShipEvents(
  state: GameState,
  viewerEmpireId: EmpireId,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  // `prevShipIds` already snapshots only viewer-owned ship ids
  // (see snapshotAll), so this comparison is owner-scoped without
  // needing extra bookkeeping. New build → green; destroyed → red.
  const viewerEmpire = state.empires.get(viewerEmpireId);
  if (!viewerEmpire) return;
  // New ships in viewer's fleet — green completion alert.
  const currentOwnIds = new Set(viewerEmpire.shipIds);
  for (const id of currentOwnIds) {
    if (diff.prevShipIds.has(id)) continue;
    const ship = state.ships.get(id);
    if (!ship) continue;
    spawn({
      category: 'green',
      message: `${ship.chassis} ready`,
      source: { kind: 'ship', id: ship.id },
    });
  }
  // Destruction — was viewer's last frame, gone from `state.ships` now.
  // (Mid-jump ships stay in `state.ships` with systemId=null, so this
  // correctly excludes outbound jumps from triggering destruction
  // alerts.)
  for (const id of diff.prevShipIds) {
    if (state.ships.has(id)) continue;
    spawn({ category: 'red', message: 'Ship lost' });
  }
}

/**
 * v4M1.6 — stalemate auto-detection (`WIN_CONDITIONS.md §1`).
 *
 * Fires a one-time amber alert when the game has gone `STALEMATE_THRESHOLD_TICKS`
 * with no significant transition. "Significant transition" here means
 * any planet-ownership flip — that's the strongest signal of strategic
 * change in the game state. Diplomatic invocations (Trade / Federation),
 * combat without ownership flips, ship destruction, and structure
 * completion all leave the timer ticking; the alert is specifically
 * about territorial deadlock.
 *
 * The alert is informational only. Player resolves via the existing
 * concede command (or by waiting it out, or by manually creating a
 * transition).
 *
 * No GameState save-format dependency — the stalemate counter lives
 * in the in-memory `AlertDiffState`. A save/reload restarts the timer
 * from zero; acceptable since the timer's purpose is "you've been
 * playing this same game for a long time without progress" and a
 * fresh load implies a fresh attention window.
 */
function detectStalemate(
  state: GameState,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  // Stalemate threshold — ticks without a significant transition
  // before the alert fires. 6000 ticks ≈ 10 minutes at 1× speed
  // (10 Hz sim). Tune by editing this constant; promote to
  // `Balance.xlsx` if playtest signal calls for a more accessible knob.
  const STALEMATE_THRESHOLD_TICKS = 6000;

  // Game already resolved — no stalemate alerts post-victory/defeat.
  if (state.gameOutcome !== 'in-progress') return;

  // Detect any planet ownership change since the previous snapshot.
  // `diff.prevPlanetOwners` was populated by the previous tick's
  // `snapshotAll` so this comparison is current-vs-previous.
  let transitionThisTick = false;
  for (const planet of state.planets.values()) {
    const prevOwner = diff.prevPlanetOwners.get(planet.id);
    if (prevOwner !== undefined && prevOwner !== planet.ownerEmpireId) {
      transitionThisTick = true;
      break;
    }
  }

  if (transitionThisTick) {
    diff.lastTransitionTick = state.tick;
    // Re-arm: future stalemate windows can trigger fresh alerts.
    diff.stalemateAlertFiredAtTick = null;
    return;
  }

  // No transition this tick. If alert already fired in the current
  // stalemate window, suppress (single-fire per stalemate).
  if (diff.stalemateAlertFiredAtTick !== null) return;

  const ticksSinceTransition = state.tick - diff.lastTransitionTick;
  if (ticksSinceTransition < STALEMATE_THRESHOLD_TICKS) return;

  const minutesElapsed = Math.floor(ticksSinceTransition / 600);
  spawn({
    category: 'amber',
    message:
      `Stalemate likely — no territorial changes for ${minutesElapsed} minutes. ` +
      `Consider conceding or breaking the deadlock.`,
  });
  diff.stalemateAlertFiredAtTick = state.tick;
}

/**
 * v4M1.3 — Surface command rejections from the per-tick `state.commandRejections`
 * buffer as amber toasts. The buffer is populated by command appliers in
 * `commands.ts` when a player-issued command silently no-ops (insufficient
 * resources, tech gate not met, slot cap, etc.) and is cleared at the start
 * of every `applyCommands` batch.
 *
 * Filter to the viewing empire so AI-issued rejections (a belt-and-braces
 * case — AI logic should validate before submitting commands) do not
 * surface to the player. Rejections with no `empireId` (an applier
 * rejected before resolving an empire) are also filtered out — they're
 * defensive guards, not player-facing constraint violations.
 *
 * No diff state needed: the buffer itself is the per-tick deltas.
 */
function detectCommandRejections(
  state: GameState,
  viewerEmpireId: EmpireId,
  spawn: (a: AlertSpawn) => void,
): void {
  if (state.commandRejections.length === 0) return;
  for (const rej of state.commandRejections) {
    if (rej.empireId === undefined) continue;
    if (rej.empireId !== viewerEmpireId) continue;
    spawn({
      category: 'amber',
      message: rej.reason,
    });
  }
}

/**
 * v4M3.4 — fire a single "Team N eliminated" toast on the falling edge
 * (team was alive last tick, now zero teammates hold a planet). Solo
 * empires (teamId === null) are out of scope — their elimination is
 * already surfaced by the per-empire ship-destruction toast chain plus
 * the end-game overlay. Per `docs/TEAMS.md §5`, the alert is purely
 * informational and fires for every team that flips, including the
 * viewer's own (the end-game overlay separately surfaces victory /
 * defeat once `gameOutcome` flips).
 *
 * Category: `red` — losing-side framing matches the Red channel
 * (own-planet flips, ship destruction) per UI_UX §1.5.
 */
function detectTeamEliminated(
  state: GameState,
  diff: AlertDiffState,
  spawn: (a: AlertSpawn) => void,
): void {
  // Per-empire planet counts, then derive per-team aliveness.
  const planetsPerEmpire = new Map<EmpireId, number>();
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    planetsPerEmpire.set(
      planet.ownerEmpireId,
      (planetsPerEmpire.get(planet.ownerEmpireId) ?? 0) + 1,
    );
  }
  const roster = getTeamRoster(state);
  for (const [teamId, memberIds] of roster) {
    let alive = false;
    for (const id of memberIds) {
      if ((planetsPerEmpire.get(id) ?? 0) > 0) {
        alive = true;
        break;
      }
    }
    const wasAlive = diff.prevTeamAlive.get(teamId);
    // Only the falling edge fires: present last tick, gone this tick.
    if (wasAlive === true && !alive) {
      spawn({
        category: 'red',
        message: `Team ${teamId} eliminated`,
      });
    }
  }
}

function snapshotAll(diff: AlertDiffState, state: GameState, viewerEmpireId: EmpireId): void {
  diff.prevPlanetOwners.clear();
  diff.prevPlanetLoyalty.clear();
  diff.prevPlanetFederatedWith.clear();
  diff.prevPlanetVassalOf.clear();
  diff.prevPlanetTradingWith.clear();
  for (const planet of state.planets.values()) {
    diff.prevPlanetOwners.set(planet.id, planet.ownerEmpireId);
    diff.prevPlanetLoyalty.set(planet.id, planet.loyalty);
    // v3M21.6 — diplomacy snapshots. tradingWith is materialised as a
    // Set so the per-tick membership check in detectDiplomacyEvents
    // is O(1) rather than O(N) across an array that's typically only
    // 1–3 entries but could grow with future multi-empire tables.
    diff.prevPlanetFederatedWith.set(planet.id, planet.federatedWith);
    diff.prevPlanetVassalOf.set(planet.id, planet.vassalOf);
    diff.prevPlanetTradingWith.set(planet.id, new Set(planet.tradingWith));
  }
  diff.prevResearchedTechs.clear();
  const empire = state.empires.get(viewerEmpireId);
  if (empire) {
    for (const tech of empire.researchedTechs) diff.prevResearchedTechs.add(tech);
  }
  diff.prevStructureLevel.clear();
  for (const struct of state.structures.values()) {
    diff.prevStructureLevel.set(struct.id, struct.level);
  }
  // Snapshot only viewer-owned ship ids so the destruction detection
  // is owner-scoped without needing extra bookkeeping.
  diff.prevShipIds.clear();
  if (empire) {
    for (const id of empire.shipIds) diff.prevShipIds.add(id);
  }
  // v4M3.4 — team-aliveness snapshot for the team-eliminated detector.
  // Solo empires (teamId === null) are excluded by `getTeamRoster`;
  // the synthetic neutral is excluded by virtue of its `teamId: null`.
  diff.prevTeamAlive.clear();
  const planetsPerEmpire = new Map<EmpireId, number>();
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    planetsPerEmpire.set(
      planet.ownerEmpireId,
      (planetsPerEmpire.get(planet.ownerEmpireId) ?? 0) + 1,
    );
  }
  const roster = getTeamRoster(state);
  for (const [teamId, memberIds] of roster) {
    let alive = false;
    for (const id of memberIds) {
      if ((planetsPerEmpire.get(id) ?? 0) > 0) {
        alive = true;
        break;
      }
    }
    diff.prevTeamAlive.set(teamId, alive);
  }
}

function humaniseKind(kind: string): string {
  return kind.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
